import React, { useState } from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { FileText, Loader2, Sparkles, Eye, Download, Mail, Lock } from 'lucide-react';

// Simple toggle component for consistency with mobile
function SimpleToggle({ checked, onChange, color = 'purple', disabled = false }) {
  const colorClasses = {
    purple: 'bg-purple-500',
    blue: 'bg-blue-500',
    indigo: 'bg-indigo-500',
    red: 'bg-red-500',
  };
  
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${checked ? colorClasses[color] : 'bg-gray-300'}`}
    >
      <span
        className={`pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`}
      />
    </button>
  );
}

export function UpdatedInvoiceContent({
  formData, handleFieldChange, preventWheelChange,
  updatedInvoiceFile, setUpdatedInvoiceFile,
  removeUpdatedInvoiceFile, setRemoveUpdatedInvoiceFile,
  editingLoad,
  generatingUpdated, handleGenerateUpdatedInvoiceClick, handleSendEmailClick,
  compact = false,
  locked = false
}) {
  const inputH = compact ? 'h-12 rounded-xl text-base' : 'h-11 rounded-xl';
  const lockedStyle = 'bg-slate-100 cursor-not-allowed opacity-70';
  const [generateEnabled, setGenerateEnabled] = useState(true);
  
  if (locked) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-2">
          <Lock className="w-4 h-4" />
          <span>This section is locked</span>
        </div>
        <div className={`grid ${compact ? 'grid-cols-1 gap-3' : 'grid-cols-3 gap-4'}`}>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Updated Kgs</Label>
            <Input value={formData.total_kgs_updated} readOnly className={`${inputH} ${lockedStyle}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Updated Tons</Label>
            <Input value={formData.total_tons_updated || ''} readOnly className={`${inputH} ${lockedStyle}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 ml-1">Amount</Label>
            <Input value={formData.updated_invoice_amount ? `₹${formData.updated_invoice_amount}` : ''} readOnly className={`${inputH} ${lockedStyle}`} />
          </div>
        </div>
        {formData.generated_updated_invoice_number && (
          <p className="text-sm text-slate-600">Invoice: {formData.generated_updated_invoice_number}</p>
        )}
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className={`grid ${compact ? 'grid-cols-1 gap-3' : 'grid-cols-3 gap-4'} items-end`}>
        <div className={compact ? '' : 'space-y-2'}>
          {!compact && <Label className="text-sm font-semibold">Updated Kgs *</Label>}
          <Input data-testid="total-kgs-updated-input" type="number" value={formData.total_kgs_updated} onChange={(e) => handleFieldChange('total_kgs_updated', e.target.value)} onWheel={preventWheelChange} placeholder={compact ? 'Updated Kgs *' : 'Updated weight'} className={`${inputH} ${formData.total_kgs_updated ? 'border-slate-900' : ''}`} />
        </div>
        <div className={compact ? '' : 'space-y-2'}>
          {!compact && <Label className="text-sm font-semibold">Updated Tons</Label>}
          <Input type="number" value={formData.total_tons_updated || ''} onChange={(e) => handleFieldChange('total_tons_updated', e.target.value)} onWheel={preventWheelChange} placeholder={compact ? 'Updated Tons' : 'Enter tons'} className={`${inputH} ${formData.total_tons_updated ? 'border-slate-900' : ''}`} />
        </div>
        <div className={compact ? '' : 'space-y-2'}>
          {!compact && <Label className="text-sm font-semibold">Updated Invoice Amount</Label>}
          <Input value={formData.updated_invoice_amount ? `₹${formData.updated_invoice_amount}` : ''} readOnly placeholder="Auto" className={`${inputH} bg-slate-50 font-semibold`} />
        </div>
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <SimpleToggle checked={generateEnabled} onChange={setGenerateEnabled} color="purple" />
          <span className="text-xs text-slate-600">Enable Generation</span>
        </div>
        <Button data-testid="gen-updated-invoice-btn" type="button" onClick={handleGenerateUpdatedInvoiceClick} disabled={!generateEnabled || !formData.total_kgs_updated || !formData.rate || generatingUpdated} className={`w-full ${compact ? 'h-12' : 'h-11'} rounded-xl font-semibold ${formData.generated_updated_invoice_file ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'} text-white disabled:opacity-50`}>
          {generatingUpdated ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {formData.generated_updated_invoice_file ? 'Regen Upd INV' : 'Gen Upd INV'}
        </Button>
      </div>
      {formData.generated_updated_invoice_file && (
        <>
          <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-xl border border-purple-200">
            <div className="flex-1 min-w-0">
              <p className={`${compact ? 'text-xs' : 'text-sm'} font-semibold text-purple-800 truncate`}>{formData.generated_updated_invoice_number}</p>
              <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-purple-600`}>₹{formData.generated_updated_invoice_amount}</p>
            </div>
            <a href={formData.generated_updated_invoice_file} target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center bg-blue-500 text-white rounded-lg" title="View PDF"><Eye className="w-4 h-4" /></a>
            <a href={formData.generated_updated_invoice_file} download={`${(formData.generated_updated_invoice_number || 'updated_invoice').replace('PREVIEW-', '')}.pdf`} className="w-8 h-8 flex items-center justify-center bg-emerald-500 text-white rounded-lg" title="Download"><Download className="w-4 h-4" /></a>
            <button type="button" onClick={() => handleSendEmailClick(formData.generated_updated_invoice_number, formData.generated_updated_invoice_amount, 'updated')} className="w-8 h-8 flex items-center justify-center bg-purple-500 text-white rounded-lg" title="Send Email"><Mail className="w-4 h-4" /></button>
          </div>
          <div className="flex justify-end">
            <button type="button" onClick={() => handleFieldChange('_clear_updated_invoice', true)} className="text-xs text-red-500 hover:text-red-700 underline">Remove Generated Invoice</button>
          </div>
        </>
      )}
      <label className={`flex flex-col items-center justify-center ${compact ? 'h-16' : 'h-20'} rounded-xl border-2 border-dashed cursor-pointer transition-all ${updatedInvoiceFile || formData.generated_updated_invoice_file ? 'bg-emerald-50 border-emerald-400' : 'bg-red-50 border-red-300 hover:border-red-400'}`}>
        <input data-testid="updated-invoice-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setUpdatedInvoiceFile(e.target.files[0])} className="hidden" />
        <FileText className={`w-6 h-6 ${updatedInvoiceFile || formData.generated_updated_invoice_file ? 'text-emerald-600' : 'text-red-500'}`} />
        <span className={`text-xs mt-1 font-medium ${updatedInvoiceFile || formData.generated_updated_invoice_file ? 'text-emerald-600' : 'text-red-500'}`}>{updatedInvoiceFile ? updatedInvoiceFile.name.substring(0, 20) + '...' : 'Updated Invoice *'}</span>
      </label>
      {!updatedInvoiceFile && editingLoad?.updated_invoice_filename && !removeUpdatedInvoiceFile && (
        <div className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2">
          <p className="text-xs text-slate-600">Current: {editingLoad.updated_invoice_filename}</p>
          <button type="button" onClick={() => setRemoveUpdatedInvoiceFile(true)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
        </div>
      )}
      {removeUpdatedInvoiceFile && editingLoad?.updated_invoice_filename && (
        <div className="flex items-center justify-between bg-red-50 rounded-lg px-3 py-2">
          <p className="text-xs text-red-600">File will be removed on save</p>
          <button type="button" onClick={() => setRemoveUpdatedInvoiceFile(false)} className="text-xs text-blue-500 hover:text-blue-700">Undo</button>
        </div>
      )}
    </div>
  );
}
